import React, { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Upload, Loader2, FileDown } from "lucide-react";

export default function PromoPool() {
  const [selected, setSelected] = useState("");
  const [programName, setProgramName] = useState("");
  const [lowThreshold, setLowThreshold] = useState(50);
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/program/config");
      setSelected(data.id);
      setProgramName(data.name);
      setLowThreshold(data.low_pool_threshold || 50);
    })();
  }, []);

  const loadPool = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    const [s, c] = await Promise.all([
      api.get(`/promo-pool/${selected}/stats`),
      api.get(`/promo-pool/${selected}/codes${filter ? `?status=${filter}` : ""}`),
    ]);
    setStats(s.data);
    setCodes(c.data);
    setLoading(false);
  }, [selected, filter]);

  useEffect(() => {
    loadPool();
  }, [loadPool]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/promo-pool/${selected}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setToast(`Uploaded ${data.inserted} (${data.duplicates} duplicates skipped)`);
    setTimeout(() => setToast(""), 3500);
    loadPool();
  };

  const selCampaign = selected ? { id: selected, name: programName, low_pool_threshold: lowThreshold } : null;
  const low = stats && selCampaign && stats.available < (selCampaign.low_pool_threshold || 50);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl">Promo Code Pool</h1>
          <p className="text-sm text-[#5c665a]">Single-use discount codes for {programName || "the program"}.</p>
        </div>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]">{toast}</div>}

      {low && (
        <div className="rounded-lg border border-[#ff3399]/30 bg-[#fff0f5] px-4 py-3 text-sm font-semibold text-[#c01974]" data-testid="pool-low-alert">
          ⚠ Only {stats.available} codes remain — below threshold ({selCampaign.low_pool_threshold}). Replenish now.
        </div>
      )}

      {stats && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6" data-testid="pool-stats">
          {[
            ["Total", stats.total, "text-[#1a1c19]"],
            ["Available", stats.available, "text-[#66cc33]"],
            ["Assigned", stats.assigned, "text-[#5c665a]"],
            ["Redeemed", stats.redeemed, "text-[#ff3399]"],
            ["Expired/Void", stats.expired + (stats.voided || 0), "text-amber-700"],
            ["7-day burn", stats.burn_7d, "text-[#1a1c19]"],
          ].map(([label, value, cls]) => (
            <div key={label} className="admin-card">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">{label}</div>
              <div className={`mt-1 text-2xl font-extrabold ${cls}`}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-1">
        <div className="admin-card">
          <h3 className="font-semibold">Bulk upload (CSV)</h3>
          <p className="mt-1 text-xs text-[#5c665a]">
            All promo codes are uploaded — none are generated from the portal. Single column of codes (header <code>code</code> optional).
          </p>
          <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#e2e8e2] bg-[#fafcfa] p-6 text-sm font-semibold text-[#5c665a] hover:border-[#66cc33] hover:text-[#66cc33]">
            <Upload className="h-4 w-4" />
            Choose CSV file
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} data-testid="pool-upload-input" />
          </label>
          <a
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#66cc33] hover:underline"
            href={`${API}/promo-pool/sample.csv`}
            download
            data-testid="pool-sample-link"
          >
            <FileDown className="h-3.5 w-3.5" /> Download sample CSV
          </a>
        </div>
      </div>

      <div className="admin-card overflow-x-auto p-0">
        <div className="flex items-center justify-between border-b border-[#e2e8e2] px-4 py-3">
          <h3 className="font-semibold">Latest codes</h3>
          <select className="admin-input max-w-[160px]" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="pool-filter">
            <option value="">All statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="redeemed">Redeemed</option>
            <option value="expired">Expired</option>
            <option value="voided">Voided</option>
          </select>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
            <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Assigned to</th><th className="px-4 py-3">Created</th></tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8e2]">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>
            ) : codes.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                <td className="px-4 py-2">
                  <span className={c.status === "available" ? "badge-green" : c.status === "redeemed" ? "badge-pink" : "badge-gray"}>{c.status}</span>
                </td>
                <td className="px-4 py-2 text-xs text-[#5c665a]">{c.assigned_to_phone || "—"}</td>
                <td className="px-4 py-2 text-xs text-[#5c665a]">{c.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
